import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  X, 
  User, 
  Building, 
  Mail, 
  Phone, 
  Check,
  Loader
} from 'lucide-react';

const CustomerSelectionPopup = ({ isOpen, onClose, onCustomerSelect }) => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'person', 'company'

  // Fetch customers when popup opens and reset tab
  useEffect(() => {
    if (isOpen) {
      setActiveTab('all');
      setSearchTerm('');
      fetchCustomers();
    }
  }, [isOpen]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: 1,
        limit: 50, // Show more customers for better selection
        search: searchTerm,
        sortBy: 'CreatedAt',
        sortOrder: 'DESC'
      });

      const response = await fetch(`/api/customers?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching customers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Search customers when search term changes
  useEffect(() => {
    if (isOpen) {
      const timeoutId = setTimeout(() => {
        fetchCustomers();
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, isOpen]);

  const handleCustomerSelect = (customer) => {
    // Extract contact information from customer
    const contactInfo = {
      ContactName: customer.CustomerType === 'Person' 
        ? `${customer.FirstName || ''} ${customer.LastName || ''}`.trim()
        : customer.CompanyName || '',
      ContactEmail: customer.PersonEmail || customer.Email || '',
      ContactPhone: customer.PersonPhone || customer.Phone || '',
      // Use JobTitle field, NOT PersonTitle (which is name prefix like "นาย")
      JobTitle: customer.JobTitle || '',
      // CompanyName: For Company type, use CompanyName; For Person type, use PersonCompanyName
      CompanyName: customer.CustomerType === 'Company' 
        ? (customer.CompanyName || customer.CustomerName || '')
        : (customer.PersonCompanyName || ''),
      Industry: customer.Industry || customer.PersonCompanyIndustry || '',
      Website: customer.Website || customer.PersonCompanyWebsite || '',
      // Include full customer object for additional processing
      customer: customer,
      CustomerID: customer.CustomerID
    };

    onCustomerSelect(contactInfo);
    onClose();
  };

  const formatCustomerName = (customer) => {
    if (customer.CustomerType === 'Person') {
      return `${customer.FirstName || ''} ${customer.LastName || ''}`.trim();
    } else {
      return customer.CompanyName || '';
    }
  };

  const formatCustomerType = (customer) => {
    return customer.CustomerType === 'Person' 
      ? t('customers.individual', 'Individual') 
      : t('customers.company', 'Company');
  };

  // Filter customers based on active tab
  const filteredCustomers = customers.filter(customer => {
    if (activeTab === 'all') return true;
    if (activeTab === 'person') return customer.CustomerType === 'Person';
    if (activeTab === 'company') return customer.CustomerType === 'Company';
    return true;
  });

  // Group customers by company for "All" tab display
  const groupedCustomers = useMemo(() => {
    if (activeTab !== 'all') {
      return { companies: [], personsWithoutCompany: [], grouped: [] };
    }

    const companies = filteredCustomers.filter(c => c.CustomerType === 'Company');
    const persons = filteredCustomers.filter(c => c.CustomerType === 'Person');
    
    // Group persons by their company name
    const personsByCompany = {};
    const personsWithoutCompany = [];
    
    persons.forEach(person => {
      const companyName = person.PersonCompanyName;
      if (companyName && companyName.trim()) {
        if (!personsByCompany[companyName]) {
          personsByCompany[companyName] = [];
        }
        personsByCompany[companyName].push(person);
      } else {
        personsWithoutCompany.push(person);
      }
    });

    // Create grouped structure: companies with their persons, then persons without companies
    const grouped = [];
    
    // Add companies with their associated persons
    companies.forEach(company => {
      const companyName = company.CompanyName || company.CustomerName;
      grouped.push({
        type: 'company',
        company: company,
        persons: personsByCompany[companyName] || []
      });
    });

    // Add companies that only exist as PersonCompanyName (not as Company type)
    Object.keys(personsByCompany).forEach(companyName => {
      const companyExists = companies.some(c => 
        (c.CompanyName || c.CustomerName) === companyName
      );
      if (!companyExists) {
        grouped.push({
          type: 'company-group',
          companyName: companyName,
          persons: personsByCompany[companyName]
        });
      }
    });

    // Add persons without companies at the end
    if (personsWithoutCompany.length > 0) {
      grouped.push({
        type: 'persons-without-company',
        persons: personsWithoutCompany
      });
    }

    return { companies, personsWithoutCompany, grouped };
  }, [activeTab, filteredCustomers]);

  // Count customers by type
  const personCount = customers.filter(c => c.CustomerType === 'Person').length;
  const companyCount = customers.filter(c => c.CustomerType === 'Company').length;
  const totalCount = customers.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('leads.pickFromCustomer', 'Pick from Customer')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('leads.pickFromCustomerSubtitle', 'Select an existing customer to fill contact information')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('leads.searchCustomers', 'Search customers by name, email, or company...')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-1 px-6">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'all'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('common.all', 'All')} ({totalCount})
            </button>
            <button
              onClick={() => setActiveTab('person')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 flex items-center space-x-2 ${
                activeTab === 'person'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <User className="h-4 w-4" />
              <span>{t('customers.individual', 'Individual')} ({personCount})</span>
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 flex items-center space-x-2 ${
                activeTab === 'company'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <Building className="h-4 w-4" />
              <span>{t('customers.company', 'Company')} ({companyCount})</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                {t('common.loading', 'Loading...')}
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400">{error}</p>
              <button
                onClick={fetchCustomers}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-8">
              {activeTab === 'person' ? (
                <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              ) : activeTab === 'company' ? (
                <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              ) : (
                <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              )}
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm 
                  ? t('leads.noCustomersFound', 'No customers found matching your search')
                  : activeTab === 'person'
                    ? t('leads.noPersonCustomers', 'No individual customers available')
                    : activeTab === 'company'
                      ? t('leads.noCompanyCustomers', 'No company customers available')
                      : t('leads.noCustomersAvailable', 'No customers available')
                }
              </p>
            </div>
          ) : activeTab === 'all' ? (
            <div className="space-y-4">
              {groupedCustomers.grouped.map((group, groupIndex) => {
                if (group.type === 'company') {
                  // Company with its persons
                  return (
                    <div key={`company-${group.company.CustomerID}`} className="space-y-2">
                      {/* Company Card */}
                      <div
                        onClick={() => handleCustomerSelect(group.company)}
                        className="p-4 border-2 border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 cursor-pointer transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <Building className="h-5 w-5 text-green-600 dark:text-green-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <h3 className="font-semibold text-gray-900 dark:text-white">
                                    {formatCustomerName(group.company)}
                                  </h3>
                                  <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                    {formatCustomerType(group.company)}
                                  </span>
                                  {group.persons.length > 0 && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      ({group.persons.length} {group.persons.length === 1 ? 'person' : 'persons'})
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 space-y-1">
                                  {group.company.Email ? (
                                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                      <Mail className="h-3 w-3" />
                                      <span>{group.company.Email}</span>
                                    </div>
                                  ) : null}
                                  {group.company.Phone ? (
                                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                      <Phone className="h-3 w-3" />
                                      <span>{group.company.Phone}</span>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Persons under this company */}
                      {group.persons.length > 0 && (
                        <div className="ml-6 space-y-2 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                          {group.persons.map((person) => (
                            <div
                              key={person.CustomerID}
                              onClick={() => handleCustomerSelect(person)}
                              className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0">
                                      <User className="h-4 w-4 text-blue-500" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                          {formatCustomerName(person)}
                                        </h4>
                                        {person.PersonTitle && (
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {person.PersonTitle}
                                          </span>
                                        )}
                                      </div>
                                      <div className="mt-1 space-y-1">
                                        {person.PersonEmail ? (
                                          <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                                            <Mail className="h-3 w-3" />
                                            <span>{person.PersonEmail}</span>
                                          </div>
                                        ) : null}
                                        {person.PersonPhone ? (
                                          <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                                            <Phone className="h-3 w-3" />
                                            <span>{person.PersonPhone}</span>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                } else if (group.type === 'company-group') {
                  // Company group (only exists as PersonCompanyName, no Company record)
                  return (
                    <div key={`company-group-${groupIndex}`} className="space-y-2">
                      {/* Company Name Header */}
                      <div className="p-3 border-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Building className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                          <h3 className="font-medium text-gray-700 dark:text-gray-300">
                            {group.companyName}
                          </h3>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            ({group.persons.length} {group.persons.length === 1 ? 'person' : 'persons'})
                          </span>
                        </div>
                      </div>
                      
                      {/* Persons under this company */}
                      <div className="ml-6 space-y-2 border-l-2 border-gray-200 dark:border-gray-700 pl-4">
                        {group.persons.map((person) => (
                          <div
                            key={person.CustomerID}
                            onClick={() => handleCustomerSelect(person)}
                            className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <User className="h-4 w-4 text-blue-500" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                        {formatCustomerName(person)}
                                      </h4>
                                      {person.PersonTitle && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {person.PersonTitle}
                                        </span>
                                      )}
                                    </div>
                                    <div className="mt-1 space-y-1">
                                      {person.PersonEmail ? (
                                        <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                                          <Mail className="h-3 w-3" />
                                          <span>{person.PersonEmail}</span>
                                        </div>
                                      ) : null}
                                      {person.PersonPhone ? (
                                        <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                                          <Phone className="h-3 w-3" />
                                          <span>{person.PersonPhone}</span>
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } else if (group.type === 'persons-without-company') {
                  // Persons without companies
                  return (
                    <div key="persons-without-company" className="space-y-2">
                      {group.persons.length > 0 && (
                        <>
                          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              {t('customers.individualsWithoutCompany', 'Individuals without Company')}
                            </h3>
                          </div>
                          {group.persons.map((person) => (
                            <div
                              key={person.CustomerID}
                              onClick={() => handleCustomerSelect(person)}
                              className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0">
                                      <User className="h-5 w-5 text-blue-500" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2">
                                        <h3 className="font-medium text-gray-900 dark:text-white">
                                          {formatCustomerName(person)}
                                        </h3>
                                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                          {formatCustomerType(person)}
                                        </span>
                                      </div>
                                      <div className="mt-1 space-y-1">
                                        {person.PersonEmail ? (
                                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                            <Mail className="h-3 w-3" />
                                            <span>{person.PersonEmail}</span>
                                          </div>
                                        ) : null}
                                        {person.PersonPhone ? (
                                          <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                            <Phone className="h-3 w-3" />
                                            <span>{person.PersonPhone}</span>
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  );
                }
                return null;
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCustomers.map((customer) => (
                <div
                  key={customer.CustomerID}
                  onClick={() => handleCustomerSelect(customer)}
                  className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {customer.CustomerType === 'Person' ? (
                            <User className="h-5 w-5 text-blue-500" />
                          ) : (
                            <Building className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="font-medium text-gray-900 dark:text-white">
                              {formatCustomerName(customer)}
                            </h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              customer.CustomerType === 'Person' 
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' 
                                : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            }`}>
                              {formatCustomerType(customer)}
                            </span>
                          </div>
                          
                          <div className="mt-1 space-y-1">
                            {customer.PersonEmail || customer.Email ? (
                              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                <Mail className="h-3 w-3" />
                                <span>{customer.PersonEmail || customer.Email}</span>
                              </div>
                            ) : null}
                            
                            {customer.PersonPhone || customer.Phone ? (
                              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                <Phone className="h-3 w-3" />
                                <span>{customer.PersonPhone || customer.Phone}</span>
                              </div>
                            ) : null}
                            
                            {customer.CustomerType === 'Person' && customer.PersonCompanyName ? (
                              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                                <Building className="h-3 w-3" />
                                <span>{customer.PersonCompanyName}</span>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <Check className="h-5 w-5 text-blue-500 opacity-0 group-hover:opacity-100" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSelectionPopup;
